import { useMemo } from 'react';

/**
 * useTimelineResolver — content-per-lane dashboard preview resolver.
 *
 * Simple rule (matches the revised 2026-04-23 content-lane model):
 *   - active = intents whose time window covers `atMinutes`
 *   - 0 active → empty
 *   - 1 active → solo (plays that content)
 *   - 2+ active → loop (plays them in a cycle)
 *
 * Track / overlapPolicy are ignored here. Backend is authoritative on what
 * TVs actually play; this is a live preview for the scrub bar / NOW line.
 */
export function resolveAt(intents, atMinutes) {
  if (!Array.isArray(intents) || intents.length === 0) {
    return { mode: 'empty', clips: [], winner: null, trace: ['no clips scheduled'] };
  }
  const active = intents
    .filter(
      (s) =>
        s &&
        s.status !== 'cancelled' &&
        Number.isFinite(s.startMinutes) &&
        Number.isFinite(s.endMinutes) &&
        atMinutes >= s.startMinutes &&
        atMinutes < s.endMinutes,
    )
    // Deterministic order for the loop cycle: createdAt ascending, then id.
    .sort((a, b) => {
      const ac = String(a.createdAt || '');
      const bc = String(b.createdAt || '');
      if (ac !== bc) return ac.localeCompare(bc);
      return String(a.id).localeCompare(String(b.id));
    });

  if (active.length === 0) {
    return { mode: 'empty', clips: [], winner: null, trace: ['nothing active'] };
  }
  if (active.length === 1) {
    const only = active[0];
    return {
      mode: 'solo',
      clips: active,
      winner: only,
      trace: [`playing: ${only.name}`],
    };
  }
  return {
    mode: 'loop',
    clips: active,
    winner: null,
    trace: [
      `${active.length} clips overlap`,
      `looping: ${active.map((c) => c.name).join(' → ')}`,
    ],
  };
}

export default function useTimelineResolver(intents, atMinutes) {
  return useMemo(() => resolveAt(intents, atMinutes), [intents, atMinutes]);
}
